import React from 'react';
import PropTypes from 'prop-types';
import {
  XAxis,
  YAxis,
  BarChart,
  Bar,
  CartesianGrid,
  ResponsiveContainer
} from 'recharts';
import Stat from '@strava/ui/Stat';
import Stats from '@strava/ui/Stats';

import Cldr from 'utils/Cldr';
import I18n from 'utils/I18n';

import styles from './styles.scss';

function getMonthFromIndex(i) {
  // By default JS converts the date to match
  // the timezone based on the user's browser
  // so here we are maintaining the UTC timezone
  // so that its consistent with the data from the server
  const date = new Date(i);
  const utcDate = new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate()
  );

  return Cldr.formatDateTime(utcDate, {
    format: 'additional',
    type: 'MMM'
  });
}

const getPath = (x, y, width, height, radius) => {
  const adjRadius = height < radius * 2 ? 2 : radius;
  const widthAdj = width - adjRadius * 2;
  const heightAdj = height - adjRadius * 2;
  const xAdj = x + adjRadius;
  return `M${xAdj},${y}
      h${widthAdj}
      a${adjRadius},${adjRadius} 0 0 1 ${adjRadius},${adjRadius}
      v${heightAdj}
      a${adjRadius},${adjRadius} 0 0 1 ${-adjRadius},${adjRadius}
      h${-widthAdj}
      a${adjRadius},${adjRadius} 0 0 1 ${-adjRadius},${-adjRadius}
      v${-heightAdj}
      a${adjRadius},${adjRadius} 0 0 1 ${adjRadius},${-adjRadius}
      z`;
};

const RoundedBar = (args) => {
  const { x, y, width, height, radius, fill } = args;
  return (
    <path d={getPath(x, y, width, height, radius)} stroke="none" fill={fill} />
  );
};

const MonthlyStats = ({
  stats: { monthlyTime, monthlyDistance, chartData }
}) => {
  const tickTextStyle = {
    fill: '#6D6D78',
    fontSize: '11px'
  };

  const barChartData =
    /*
      TODO: Check to see if we're displaying correct stats
      chartData is returngin null, but monthly stat numbers are
      displaying, so there might be a discrepency in what we're getting
      back from the view class.
    */
    chartData &&
    chartData.map((i) => ({
      month: getMonthFromIndex(i.month),
      miles: i.miles
    }));

  const timeSpan = <span dangerouslySetInnerHTML={{ __html: monthlyTime }} />;

  return (
    <div>
      {/* Will refactor into tab component  */}
      <h3>{I18n.t('strava.athletes.show_public.current_month')}</h3>

      <Stats listClassName={styles.currMonthStats}>
        <Stat
          value={monthlyDistance}
          label={I18n.t('strava.athletes.show_public.distance')}
          type="stat-lg"
        />
        <Stat
          value={timeSpan}
          label={I18n.t('strava.athletes.show_public.moving_time')}
          type="stat-lg"
        />
      </Stats>

      {chartData && (
        <div className={styles.barChart}>
          <ResponsiveContainer height={225}>
            <BarChart
              data={barChartData}
              className={styles.monthlyChart}
              barGap={12}
            >
              <Bar
                dataKey="miles"
                fill="#f0f0f5"
                minPointSize={4}
                shape={<RoundedBar />}
                radius={4}
              />
              <CartesianGrid
                vertical={false}
                strokeDasharray="2 3"
                x={0}
                width={800}
              />
              <XAxis
                dataKey="month"
                tick={tickTextStyle}
                stroke="none"
                minTickGap={0}
              />
              <YAxis
                dataKey="miles"
                tick={{ ...tickTextStyle, textAnchor: 'start' }}
                tickCount={3}
                stroke="none"
                width={20}
                dy={10}
                dx={-17}
                textAnchor="end"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

MonthlyStats.propTypes = {
  stats: PropTypes.shape({
    monthlyTime: PropTypes.string,
    monthlyDistance: PropTypes.string,
    chartData: PropTypes.arrayOf(PropTypes.object)
  })
};

MonthlyStats.defaultProps = {
  stats: {
    monthlyTime: '',
    monthlyDistance: '',
    chartData: []
  }
};

export default MonthlyStats;
